/* BOHDY build-your-own pastry box (sections/pastry-box.liquid).
 *
 * The customer picks a box size (a product variant whose title carries the
 * count, "Box of 6"), fills it from the pastry cards, and chooses a pickup
 * day. The form posts to /cart/add with the picks and the pickup date as
 * line-item properties, so staff see both on the order.
 *
 * Pickup days are computed in store-local time: an order before the cutoff
 * hour can pick up tomorrow, a later one starts the day after. Days the store
 * is closed (weekly hours rules + date overrides from theme settings, same
 * format the homepage "open now" chip reads) are skipped.
 *
 * With delivery offered (data-delivery-window on the section), the same day
 * serves pickup or local delivery, so the property is the neutral "Date" (the
 * section names the input) and the copy covers both. The builder never asks
 * pickup-or-delivery: checkout does, once, for every product in the cart.
 */
(function () {
  'use strict';

  var DAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
  var WEEKDAY_LONG = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  var WEEKDAY_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  var MONTH_LONG = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  var MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  // "Mo-Fr 07:00-18:00" -> [{ days: [1..5] }]; only the days matter here.
  function parseRules(text) {
    var out = [];
    (text || '').split(/\r?\n/).forEach(function (line) {
      var m = line.trim().match(/^([A-Za-z,\-]+)\s+(\d{1,2}):(\d{2})\s*-\s*(\d{1,2}):(\d{2})$/);
      if (!m) return;
      var days = [];
      m[1].split(',').forEach(function (part) {
        var ends = part.split('-');
        var a = DAYS.indexOf(ends[0]);
        var b = ends.length > 1 ? DAYS.indexOf(ends[1]) : a;
        if (a < 0 || b < 0) return;
        var d = a;
        for (var guard = 0; guard < 7; guard++) {
          days.push(d);
          if (d === b) break;
          d = (d + 1) % 7;
        }
      });
      if (days.length) out.push({ days: days });
    });
    return out;
  }

  // "2026-11-26 closed" / "2026-07-03 06:00-18:00" -> { '2026-11-26': true (closed) | false (open) }
  function parseOverrides(text) {
    var map = {};
    (text || '').split(/\r?\n/).forEach(function (line) {
      var m = line.trim().match(/^(\d{4}-\d{2}-\d{2})\s+(?:(closed)|\d{1,2}:\d{2}\s*-\s*\d{1,2}:\d{2})$/i);
      if (m) map[m[1]] = Boolean(m[2]);
    });
    return map;
  }

  function pad(n) { return (n < 10 ? '0' : '') + n; }

  // Wall-clock date + hour "now" in the store's time zone.
  function storeNow(now, timeZone) {
    var parts = {};
    new Intl.DateTimeFormat('en-US', {
      timeZone: timeZone,
      year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', hourCycle: 'h23'
    }).formatToParts(now).forEach(function (p) { parts[p.type] = p.value; });
    return { y: +parts.year, m: +parts.month, d: +parts.day, hour: +parts.hour % 24 };
  }

  // Bookable pickup days. Dates are built at UTC noon so day arithmetic never
  // crosses a DST edge; only the calendar date is used.
  function pickupDates(opts) {
    var local = storeNow(opts.now || new Date(), opts.timeZone || 'America/New_York');
    var rules = parseRules(opts.hours);
    var overrides = parseOverrides(opts.overrides);
    var first = local.hour < opts.cutoffHour ? 1 : 2;
    var out = [];
    for (var k = first; k <= opts.daysAhead; k++) {
      var date = new Date(Date.UTC(local.y, local.m - 1, local.d + k, 12));
      var key = date.getUTCFullYear() + '-' + pad(date.getUTCMonth() + 1) + '-' + pad(date.getUTCDate());
      var wd = date.getUTCDay();
      var open;
      if (Object.prototype.hasOwnProperty.call(overrides, key)) open = !overrides[key];
      else if (!rules.length) open = true;
      else open = rules.some(function (r) { return r.days.indexOf(wd) !== -1; });
      if (!open) continue;
      out.push({
        key: key,
        label: (k === 1 ? 'Tomorrow, ' : '') + WEEKDAY_SHORT[wd] + ', ' + MONTH_SHORT[date.getUTCMonth()] + ' ' + date.getUTCDate(),
        full: WEEKDAY_LONG[wd] + ', ' + MONTH_LONG[date.getUTCMonth()] + ' ' + date.getUTCDate() + ', ' + date.getUTCFullYear()
      });
    }
    return out;
  }

  // Summary copy: pickup only, or pickup / local delivery when delivery is offered.
  function dayCopy(deliveryWindow) {
    if (deliveryWindow) {
      return {
        missing: 'choose a day',
        ready: function (label) { return 'Ready ' + label + ': pick up, or local delivery ' + deliveryWindow + '.'; }
      };
    }
    return {
      missing: 'choose a pickup day',
      ready: function (label) { return 'Ready for pickup ' + label + '.'; }
    };
  }

  function sizeOf(title) {
    var m = String(title || '').match(/\d+/);
    return m ? parseInt(m[0], 10) : 0;
  }

  function init(root) {
    var form = root.querySelector('form');
    var variantInput = root.querySelector('[data-box-variant]');
    var picksInput = root.querySelector('[data-box-picks]');
    var dateValue = root.querySelector('[data-box-date-value]');
    var dateSelect = root.querySelector('[data-box-date]');
    var bar = root.querySelector('[data-box-bar]');
    var countEl = root.querySelector('[data-box-count]');
    var summaryEl = root.querySelector('[data-box-summary]');
    var fillBtn = root.querySelector('[data-box-fill]');
    var addBtn = root.querySelector('[data-box-add]');
    var sizeButtons = Array.prototype.slice.call(root.querySelectorAll('[data-size-option]'));
    var cards = Array.prototype.slice.call(root.querySelectorAll('[data-pastry]'));
    if (!form || !variantInput || !addBtn) return;

    var copy = dayCopy(root.getAttribute('data-delivery-window') || '');

    var qty = cards.map(function () { return 0; });
    var pickOrder = []; // card indexes in the order they were added; trimming pops from the end
    var active = sizeButtons.filter(function (b) { return b.classList.contains('is-active'); })[0] || sizeButtons[0];
    var size = active ? sizeOf(active.getAttribute('data-variant-title')) : 0;
    var dates = [];

    // Rebuild the bookable days from the current time, keeping the customer's
    // choice only if it is still bookable. Runs at load, when the tab comes
    // back into view, and on submit, so a page left open past the cutoff (or
    // overnight) cannot book a day without the promised lead time.
    function refreshDates() {
      dates = pickupDates({
        now: new Date(),
        timeZone: root.getAttribute('data-timezone'),
        cutoffHour: parseInt(root.getAttribute('data-cutoff-hour'), 10) || 14,
        daysAhead: parseInt(root.getAttribute('data-days-ahead'), 10) || 14,
        hours: root.getAttribute('data-hours'),
        overrides: root.getAttribute('data-hours-overrides')
      });
      if (!dateSelect) return;
      var keep = dateSelect.value;
      while (dateSelect.options.length > 1) dateSelect.remove(1);
      dates.forEach(function (d) {
        var opt = document.createElement('option');
        opt.value = d.key;
        opt.textContent = d.label;
        dateSelect.appendChild(opt);
      });
      dateSelect.value = dates.some(function (d) { return d.key === keep; }) ? keep : '';
    }

    refreshDates();
    if (dateSelect) dateSelect.addEventListener('change', render);
    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState === 'visible') { refreshDates(); render(); }
    });

    function total() { return qty.reduce(function (a, b) { return a + b; }, 0); }

    function selectedDate() {
      var key = dateSelect ? dateSelect.value : '';
      return dates.filter(function (d) { return d.key === key; })[0] || null;
    }

    function picksText() {
      var lines = [];
      cards.forEach(function (card, i) {
        if (qty[i]) lines.push(qty[i] + ' × ' + card.getAttribute('data-name'));
      });
      return lines.join(', ');
    }

    function add(i) {
      if (total() >= size) return;
      qty[i] += 1;
      pickOrder.push(i);
    }

    function remove(i) {
      if (!qty[i]) return;
      qty[i] -= 1;
      var at = pickOrder.lastIndexOf(i);
      if (at !== -1) pickOrder.splice(at, 1);
    }

    function render() {
      var n = total();
      var full = size > 0 && n >= size;
      var date = selectedDate();
      if (bar) bar.style.width = size ? Math.min(100, (n / size) * 100) + '%' : '0';
      if (countEl) countEl.textContent = full ? 'Box full: ' + n + ' of ' + size : n + ' of ' + size + ' picked';
      if (fillBtn) fillBtn.disabled = full || !cards.length;
      cards.forEach(function (card, i) {
        card.querySelector('[data-qty]').textContent = qty[i];
        card.querySelector('[data-minus]').disabled = !qty[i];
        card.querySelector('[data-plus]').disabled = full;
        card.classList.toggle('is-picked', qty[i] > 0);
      });
      var missing = [];
      if (!full) missing.push('pick ' + (size - n) + ' more');
      if (!date) missing.push(copy.missing);
      if (summaryEl) {
        summaryEl.textContent = missing.length
          ? 'To finish: ' + missing.join(' and ') + '.'
          : copy.ready(date.label.replace(/^Tomorrow, /, 'tomorrow, '));
      }
      addBtn.disabled = missing.length > 0;
    }

    sizeButtons.forEach(function (btn) {
      btn.addEventListener('click', function () {
        if (btn.disabled) return;
        sizeButtons.forEach(function (b) {
          b.classList.toggle('is-active', b === btn);
          b.setAttribute('aria-checked', b === btn ? 'true' : 'false');
        });
        variantInput.value = btn.getAttribute('data-variant-id');
        size = sizeOf(btn.getAttribute('data-variant-title'));
        // Shrinking the box drops the most recent picks first.
        while (total() > size && pickOrder.length) remove(pickOrder[pickOrder.length - 1]);
        render();
      });
    });

    cards.forEach(function (card, i) {
      card.querySelector('[data-plus]').addEventListener('click', function () { add(i); render(); });
      card.querySelector('[data-minus]').addEventListener('click', function () { remove(i); render(); });
    });

    if (fillBtn) {
      // Chef's choice: spread the remaining spots across the least-picked pastries.
      fillBtn.addEventListener('click', function () {
        while (total() < size && cards.length) {
          var least = 0;
          for (var i = 1; i < qty.length; i++) if (qty[i] < qty[least]) least = i;
          add(least);
        }
        render();
      });
    }

    form.addEventListener('submit', function (event) {
      refreshDates();
      var date = selectedDate();
      if (total() !== size || !date) {
        event.preventDefault();
        render();
        return;
      }
      picksInput.value = picksText();
      dateValue.value = date.full;
    });

    render();
  }

  window.BohdyPastryBox = {
    pickupDates: pickupDates,
    parseRules: parseRules,
    parseOverrides: parseOverrides,
    dayCopy: dayCopy
  };

  function boot() {
    Array.prototype.forEach.call(document.querySelectorAll('[data-pastry-box]'), init);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();